import type { OrderDetail } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { formatCount, formatDateTime, formatWon } from '@/shared/lib';
import { ContentCard, cn, DesignThumbnail, detailListStyles as styles } from '@/shared/ui';

interface OrderSummaryProps {
  readonly order: OrderDetail;
  readonly onQuoteOpen: (quoteRequestId: string) => void;
}

/**
 * 주문 요약 — 견적 썸네일(없거나 못 불러오면 "미리보기 준비 중")과 견적 번호(견적 상세로 이동)·상품·수량·금액·출고 예정일,
 * 처리 중 취소 요청의 환급 기한, 취소·환불 일시.
 */
export const OrderSummary = ({ order, onQuoteOpen }: OrderSummaryProps): ReactElement => (
  <ContentCard title="주문 요약">
    <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
      <DesignThumbnail
        url={order.thumbnailUrl}
        alt={`${order.orderNumber} 디자인 미리보기`}
        className="size-32 shrink-0"
      />
      <dl className={cn(styles.list, 'min-w-0 flex-1')}>
        <dt className={styles.term}>견적 번호</dt>
        <dd>
          <button
            type="button"
            className="font-medium underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground focus-visible:decoration-foreground focus-visible:outline-none"
            onClick={() => onQuoteOpen(order.quoteRequestId)}
          >
            {order.quoteNumber}
          </button>
        </dd>
        <dt className={styles.term}>상품</dt>
        <dd>{order.productName}</dd>
        <dt className={styles.term}>수량</dt>
        <dd>{formatCount(order.quantity)}개</dd>
        <dt className={styles.term}>금액</dt>
        <dd className="tabular-nums">{formatWon(order.amount)}</dd>
        <dt className={styles.term}>출고 예정일</dt>
        <dd>{order.expectedShipDate ?? <span className={styles.empty}>미정</span>}</dd>
        {order.pendingCancelRequest ? (
          <>
            <dt className={styles.term}>환급 기한</dt>
            <dd>{order.pendingCancelRequest.refundDueDate}</dd>
          </>
        ) : null}
        {order.canceledAt ? (
          <>
            <dt className={styles.term}>주문 취소 일시</dt>
            <dd>{formatDateTime(order.canceledAt)}</dd>
          </>
        ) : null}
        {order.refundedAt ? (
          <>
            <dt className={styles.term}>환불 일시</dt>
            <dd>{formatDateTime(order.refundedAt)}</dd>
          </>
        ) : null}
      </dl>
    </div>
  </ContentCard>
);
